import { Component, inject, OnInit, signal } from '@angular/core';
import { DatasetCard, DatasetCardData } from '../../datasets/dataset-card/dataset-card';
import { ModelCard, ModelCardData } from '../../models/model-card/model-card';
import { SkeletonComponent } from '../../../shared/components/skeleton/skeleton.component';
import { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';
import { HelperService } from '../../../shared/services/helper.service';

const ITEMS_PER_PAGE = 3;
const SORT_NEWEST = 'itemCreatedAt:desc;';

@Component({
  selector: 'app-explore-section',
  standalone: true,
  imports: [DatasetCard, ModelCard, SkeletonComponent],
  templateUrl: './explore-section.html',
  styleUrl: './explore-section.scss',
})
export class ExploreSection implements OnInit {
  private readonly catalogueApiService = inject(CatalogueApiService);
  private readonly formatService = inject(FormatService);
  private readonly helperService = inject(HelperService);

  readonly badgeIconPath = '/icons/box-2.svg';
  readonly datasetsTabIconPath = '/icons/dataset.svg';
  readonly modelsTabIconPath = '/icons/wire.svg';
  readonly chevronIconPath = '/icons/chevron-down-black.svg';

  readonly activeTab = signal<'datasets' | 'models'>('datasets');
  /** API page (1-based), aligned with `DatasetHome` / `ModelHome`. */
  readonly datasetsPage = signal(1);
  readonly modelsPage = signal(1);
  readonly datasetsTotalPages = signal(1);
  readonly modelsTotalPages = signal(1);

  readonly datasetCards = signal<DatasetCardData[]>([]);
  readonly modelCards = signal<ModelCardData[]>([]);
  readonly datasetsLoading = signal(false);
  readonly modelsLoading = signal(false);

  ngOnInit(): void {
    void this.loadDatasets();
    void this.loadModels();
  }

  switchTab(tab: 'datasets' | 'models'): void {
    this.activeTab.set(tab);
    if (tab === 'datasets') {
      this.datasetsPage.set(1);
      void this.loadDatasets();
    } else {
      this.modelsPage.set(1);
      void this.loadModels();
    }
  }

  prevPage(): void {
    if (this.activeTab() === 'datasets') {
      const p = this.datasetsPage();
      if (p > 1) {
        this.datasetsPage.set(p - 1);
        void this.loadDatasets();
      }
    } else {
      const p = this.modelsPage();
      if (p > 1) {
        this.modelsPage.set(p - 1);
        void this.loadModels();
      }
    }
  }

  nextPage(): void {
    if (this.activeTab() === 'datasets') {
      const p = this.datasetsPage();
      const max = this.datasetsTotalPages();
      if (p < max) {
        this.datasetsPage.set(p + 1);
        void this.loadDatasets();
      }
    } else {
      const p = this.modelsPage();
      const max = this.modelsTotalPages();
      if (p < max) {
        this.modelsPage.set(p + 1);
        void this.loadModels();
      }
    }
  }

  hasPrev(): boolean {
    return this.activeTab() === 'datasets' ? this.datasetsPage() > 1 : this.modelsPage() > 1;
  }

  hasNext(): boolean {
    return this.activeTab() === 'datasets'
      ? this.datasetsPage() < this.datasetsTotalPages()
      : this.modelsPage() < this.modelsTotalPages();
  }

  private async loadDatasets(): Promise<void> {
    this.datasetsLoading.set(true);
    try {
      const response = await this.catalogueApiService.searchAllAssets(
        '',
        ['Dataset'],
        [],
        this.datasetsPage(),
        ITEMS_PER_PAGE,
        SORT_NEWEST,
        [] as SearchCriteria[]
      );
      const items = this.extractItemsFromResponse(response);
      this.datasetCards.set(items.map((item: unknown) => this.mapApiItemToDatasetCard(item)));
      this.applyPaginationFromResponse(response, 'datasets');
    } catch (e) {
      console.error('ExploreSection: failed to load datasets', e);
      this.datasetCards.set([]);
      this.datasetsTotalPages.set(1);
    } finally {
      this.datasetsLoading.set(false);
    }
  }

  private async loadModels(): Promise<void> {
    this.modelsLoading.set(true);
    try {
      const response = await this.catalogueApiService.searchAllAssets(
        '',
        ['Model'],
        [],
        this.modelsPage(),
        ITEMS_PER_PAGE,
        SORT_NEWEST,
        [] as SearchCriteria[]
      );
      const items = this.extractItemsFromResponse(response);
      this.modelCards.set(items.map((item: unknown) => this.mapApiItemToModelCard(item)));
      this.applyPaginationFromResponse(response, 'models');
    } catch (e) {
      console.error('ExploreSection: failed to load models', e);
      this.modelCards.set([]);
      this.modelsTotalPages.set(1);
    } finally {
      this.modelsLoading.set(false);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private applyPaginationFromResponse(response: any, kind: 'datasets' | 'models'): void {
    const pi = response?.paginationInfo;
    if (pi) {
      let totalPages = 1;
      if (pi.totalPages !== undefined) {
        totalPages = Math.max(1, pi.totalPages);
      } else if (pi.totalCount !== undefined) {
        totalPages = Math.max(1, Math.ceil(pi.totalCount / ITEMS_PER_PAGE));
      }
      if (kind === 'datasets') this.datasetsTotalPages.set(totalPages);
      else this.modelsTotalPages.set(totalPages);
      return;
    }
    const result = response?.result;
    if (result?.totalCount !== undefined) {
      const totalPages = Math.max(1, Math.ceil(result.totalCount / ITEMS_PER_PAGE));
      if (kind === 'datasets') this.datasetsTotalPages.set(totalPages);
      else this.modelsTotalPages.set(totalPages);
    } else if (typeof response?.totalCount === 'number') {
      const totalPages = Math.max(1, Math.ceil(response.totalCount / ITEMS_PER_PAGE));
      if (kind === 'datasets') this.datasetsTotalPages.set(totalPages);
      else this.modelsTotalPages.set(totalPages);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractItemsFromResponse(response: any): any[] {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.result)) return response.result;
    if (response.result && typeof response.result === 'object' && Array.isArray(response.result.results)) {
      return response.result.results;
    }
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.items)) return response.items;
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapApiItemToDatasetCard(item: any): DatasetCardData {
    const id = String(item?.id ?? '');
    const title = String(item?.label ?? item?.name ?? 'Untitled');
    const organization = String(
      item?.provider ??
        item?.organisation ??
        item?.organization ??
        item?.provider_name ??
        item?.organizationName ??
        ''
    );
    const rawDesc =
      (item?.shortDescription as string) ??
      (item?.short_description as string) ??
      (item?.description as string) ??
      '';
    const shortDescription =
      typeof rawDesc === 'string' && rawDesc.trim()
        ? rawDesc.length > 120
          ? rawDesc.trim().slice(0, 120) + '…'
          : rawDesc.trim()
        : undefined;

    const resourceServerTags: string[] = [];
    const rs = item?.resourceServer;
    if (rs) {
      const servers = Array.isArray(rs) ? rs : [rs];
      for (const server of servers) {
        if (server?.name && !resourceServerTags.includes(String(server.name).toUpperCase())) {
          resourceServerTags.push(String(server.name).toUpperCase());
        }
        if (server?.type && !resourceServerTags.includes(String(server.type).toUpperCase())) {
          resourceServerTags.push(String(server.type).toUpperCase());
        }
      }
    }

    const apiTags: string[] = [];
    if (Array.isArray(item?.tags)) {
      apiTags.push(...item.tags);
    }

    const fileFormat = String(item?.fileFormat ?? item?.format ?? '');
    const dateLabel = this.formatService.formatDateForCard(
      item?.itemCreatedAt ?? item?.createdAt ?? new Date()
    );
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);
    const metricsData = item?.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item?.likeCount ?? item?.likes ?? 0,
      downloads: metricsData.downloads ?? item?.downloadCount ?? item?.downloads ?? 0,
      files: item?.fileCount ?? item?.files ?? 0,
      views: metricsData.views ?? item?.viewCount ?? item?.views ?? 0,
    };

    return {
      id,
      title,
      shortDescription,
      organization,
      tags: resourceServerTags.length > 0 ? resourceServerTags.slice(0, 3) : apiTags.slice(0, 3),
      fileType: fileFormat || undefined,
      dateLabel,
      badge,
      badgeColor,
      link: `/dataset/${id}`,
      metrics,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapApiItemToModelCard(item: any): ModelCardData {
    const id = String(item?.id ?? '');
    const title = String(item?.label ?? item?.name ?? 'Untitled');
    const organization = String(
      item?.provider ??
        item?.organisation ??
        item?.organization ??
        item?.provider_name ??
        item?.organizationName ??
        ''
    );
    const rawDesc =
      (item?.shortDescription as string) ??
      (item?.short_description as string) ??
      (item?.description as string) ??
      '';
    const shortDescription =
      typeof rawDesc === 'string' && rawDesc.trim()
        ? rawDesc.length > 120
          ? rawDesc.trim().slice(0, 120) + '…'
          : rawDesc.trim()
        : undefined;

    let industry = '';
    if (item?.industry) {
      if (Array.isArray(item.industry)) {
        industry = item.industry
          .filter((v: unknown) => v !== null && v !== undefined && v !== '')
          .join(', ');
      } else if (typeof item.industry === 'string' && item.industry.trim() !== '') {
        industry = item.industry.trim();
      }
    }

    const apiTags: string[] = [];
    if (Array.isArray(item?.tags)) {
      apiTags.push(...item.tags);
    }

    const modelType = String(item?.modelType ?? '').trim() || undefined;
    const language =
      String(item?.codeLanguage ?? item?.fileFormat ?? item?.format ?? '').trim() || undefined;

    const date = this.formatService.formatDateForModelCard(
      item?.itemCreatedAt ?? item?.createdAt ?? new Date()
    );

    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);

    const metricsData = item?.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item?.likeCount ?? item?.likes ?? 0,
      downloads: metricsData.downloads ?? item?.downloadCount ?? item?.downloads ?? 0,
      files: item?.fileCount ?? item?.files ?? 0,
      views: metricsData.views ?? item?.viewCount ?? item?.views ?? 0,
    };

    return {
      id,
      title,
      shortDescription,
      organization,
      industry: industry || undefined,
      tags: apiTags.slice(0, 3),
      modelType,
      language,
      date,
      badge,
      badgeColor,
      link: `/model/${id}`,
      metrics,
    };
  }
}
